import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CruiseReservation, CruiseItineraryStop, TripData } from "./useTrips";
import type { Json } from "@/integrations/supabase/types";

interface CruiseEnrichment {
  cruise_title?: string;
  itinerary_stops: CruiseItineraryStop[];
}

function applyCruiseEnrichment(cruise: CruiseReservation, enrichment: CruiseEnrichment): CruiseReservation {
  return {
    ...cruise,
    cruise_title: enrichment.cruise_title || cruise.cruise_title,
    itinerary_stops: enrichment.itinerary_stops.length > 0 ? enrichment.itinerary_stops : cruise.itinerary_stops,
    enriched: true,
  };
}

async function fetchCruiseEnrichment(cruise: CruiseReservation): Promise<CruiseEnrichment | null> {
  try {
    const { data, error } = await supabase.functions.invoke('enrich-cruise', {
      body: {
        ship_name: cruise.ship_name,
        cruise_line: cruise.cruise_line,
        departure_date: cruise.departure_date,
        arrival_date: cruise.arrival_date,
      },
    });

    if (error) {
      console.error('Error calling enrich-cruise:', error);
      return null;
    }

    return data?.enrichment as CruiseEnrichment | null;
  } catch (err) {
    console.error('Exception calling enrich-cruise:', err);
    return null;
  }
}

export function useEnrichCruise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      cruiseIndex,
      cruise,
    }: {
      tripId: string;
      cruiseIndex: number;
      cruise: CruiseReservation;
    }) => {
      const enrichment = await fetchCruiseEnrichment(cruise);

      if (!enrichment) {
        return { enriched: false, message: 'No cruise itinerary data found' };
      }

      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      const cruises = [...(tripData.cruises || [])];

      cruises[cruiseIndex] = applyCruiseEnrichment(cruises[cruiseIndex], enrichment);

      const updatedTripData = { ...tripData, cruises };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enriched: true, enrichment };
    },
    onSuccess: (result, variables) => {
      if (result.enriched) {
        queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast.success("Cruise itinerary loaded successfully");
      } else {
        toast.info(result.message || "No cruise itinerary data available");
      }
    },
    onError: (error) => {
      console.error("Cruise enrichment error:", error);
      toast.error("Failed to load cruise itinerary");
    },
  });
}

export function useAutoEnrichCruise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      cruiseIndex,
      cruise,
    }: {
      tripId: string;
      cruiseIndex: number;
      cruise: CruiseReservation;
    }) => {
      if (cruise.enriched) {
        return { enriched: false, skipped: true };
      }

      const enrichment = await fetchCruiseEnrichment(cruise);

      if (!enrichment) {
        return { enriched: false, skipped: false };
      }

      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      const cruises = [...(tripData.cruises || [])];

      if (cruiseIndex >= cruises.length) {
        return { enriched: false, skipped: true };
      }

      cruises[cruiseIndex] = applyCruiseEnrichment(cruises[cruiseIndex], enrichment);

      const updatedTripData = { ...tripData, cruises };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enriched: true, enrichment };
    },
    onSuccess: (result, variables) => {
      if (result.enriched) {
        queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast.success("Cruise itinerary loaded automatically");
      }
    },
    onError: (error) => {
      console.error("Auto-enrich cruise error:", error);
    },
  });
}
